import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { T, F, th as palette, Over, Btn } from "@/lib/bricks";
import { Section, Stats, List, Row, Empty, TextLink, humanize, figLabel } from "@/components/calm/Calm";
import { fmtDay } from "@/lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import DeskBoard from "@/components/desk/DeskBoard";
import { useAuth } from "@/contexts/AuthContext";

export default function OverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '', allocation: 10 });
  const navigate = useNavigate();
  const { user } = useAuth();
  // Agent invites and per-agent usage only mean something to an office or a team.
  const leadsAgents = !!user && (user.role === 'manager' || user.role === 'admin' || user.subscription_type === 'team');
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  useEffect(() => {
    api.get("/dashboard/overview")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleInvite = async () => {
    if (!inviteData.email) return toast.error("Email is required");
    try {
      await api.post("/agents/invite", inviteData);
      toast.success(`Invitation sent to ${inviteData.email}`);
      setInviteModal(false);
      setInviteData({ name: '', email: '', allocation: 10 });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send invitation"); }
  };

  const deskActions = leadsAgents ? (
    <TextLink testid="invite-agent" onClick={() => setInviteModal(true)}>Invite agent</TextLink>
  ) : null;

  if (loading) return (
    <div data-testid="overview-page" style={{ display:'flex', flexDirection:'column', gap:24, fontFamily:F.display, color:p.ink }}>
      <DeskBoard actions={deskActions} />
    </div>
  );
  if (!data) return (
    <div data-testid="overview-page" style={{ display:'flex', flexDirection:'column', gap:24, fontFamily:F.display, color:p.ink }}>
      <DeskBoard actions={deskActions} />
    </div>
  );

  const s = data.subscription || {};
  const cycleUsed  = s.shoots_used || 0;
  const cycleTotal = s.plan_limit  || 1;

  const used = (data.agent_usage || []).slice(0, 6);
  const recent = (data.recent_activity || []).slice(0, 8);
  const shots = (data.recent_deliveries || []).filter(d => d?.thumbnail_url).slice(0, 6);

  return (
    <div data-testid="overview-page" style={{ display:'flex', flexDirection:'column', gap:32, fontFamily:F.display, color:p.ink, paddingBottom:96 }}>

      {/* The desk — role-aware KPIs + widgets from real data */}
      <DeskBoard actions={deskActions} />

      <Section title="Shoot allocation" note="This billing cycle"
        action={data.overage_delivery_count > 0 ? (
          <span style={{ color: p.attn }}><TextLink onClick={() => navigate('/billing')}>{data.overage_delivery_count} overage shoot{data.overage_delivery_count !== 1 ? 's' : ''}, view billing</TextLink></span>
        ) : null}>
        <Stats testid="overview-allocation" items={[
          { label: `Used of ${cycleTotal}`, value: String(cycleUsed), attention: cycleUsed > cycleTotal },
          ...(leadsAgents ? [
            { label: 'Agents booking', value: String(data.active_agents ?? 0) },
            { label: 'Shoots per agent', value: String(data.avg_usage_per_agent ?? 0) },
          ] : []),
        ]} />
      </Section>

      {leadsAgents && <Section title="Agent usage" action={<TextLink onClick={() => navigate('/agents')}>All agents</TextLink>}>
        {!used.length ? <Empty>No shoots booked this cycle.</Empty> : (
          <List testid="overview-agent-usage">
            {used.map(agent => {
              const pct = agent.allocation > 0 ? Math.round((agent.shoots_used / agent.allocation) * 100) : 0;
              return (
                <Row key={agent.name} wide={false} title={agent.name}
                  meta={[figLabel(`${agent.shoots_used} of ${agent.allocation}`, 'shoots this cycle')]}
                  status={pct > 90 ? 'Near limit' : pct > 50 ? 'Regular' : 'Light'} attention={pct > 90} />
              );
            })}
          </List>
        )}
      </Section>}

      <Section title="Latest deliveries" action={<TextLink onClick={() => navigate('/properties')}>Media library</TextLink>}>
        {shots.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-6" style={{ gap: 6 }}>
            {shots.map((d, i) => (
              <button key={i} type="button" onClick={() => navigate(d.property_id ? `/properties/${d.property_id}` : '/properties')} style={{ padding:0, border:'none', background:'none', cursor:'pointer' }} aria-label={d.address || 'Delivery'}>
                <img src={d.thumbnail_url} alt="" style={{ width:'100%', aspectRatio:'4 / 3', objectFit:'cover', display:'block', borderRadius: 2 }}/>
              </button>
            ))}
          </div>
        ) : <Empty>Delivered photos land in the media library.</Empty>}
        <div style={{ fontSize: 12.5, color: p.ink3, marginTop: 12 }}>
          Favourite photographers get first pick of your office's shoots. <TextLink onClick={() => navigate('/vendors')}>Manage favourites</TextLink>
        </div>
      </Section>

      {recent.length > 0 && (
        <Section title="Recent activity">
          <List testid="overview-activity">
            {recent.map((act, i) => (
              <Row key={i}
                title={act.message || act.address || humanize(act.type)}
                meta={[act.message ? [act.address, act.package].filter(Boolean).join(', ') : act.package, act.agent].filter(Boolean)}
                next={(act.timestamp || act.at) ? [{ text: fmtDay(act.timestamp || act.at), figure: true }] : null}
                status={(act.message || act.address) ? humanize(act.type) : ''} />
            ))}
          </List>
        </Section>
      )}

      {/* Invite Agent Modal */}
      <Dialog open={inviteModal} onOpenChange={setInviteModal}>
        <DialogContent style={{ maxWidth: 400, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, letterSpacing: -0.3, color: p.ink }}>Invite agent</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              They’ll get an email invite.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 20 }}>
            <div>
              <Over color={p.ink3}>Agent name</Over>
              <input
                type="text" value={inviteData.name}
                onChange={e => setInviteData({ ...inviteData, name: e.target.value })}
                placeholder="Full name"
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <Over color={p.ink3}>Email address</Over>
              <input
                type="email" value={inviteData.email}
                onChange={e => setInviteData({ ...inviteData, email: e.target.value })}
                placeholder="marisol@example.com"
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <Over color={p.ink3}>Starting allocation</Over>
              <input
                type="number" value={inviteData.allocation}
                onChange={e => setInviteData({ ...inviteData, allocation: parseInt(e.target.value) })}
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <DialogFooter style={{ marginTop: 32, gap: 10 }}>
            <Btn variant="ghost" onClick={() => setInviteModal(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleInvite}>Send invite</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
